import { formatCalendarDate, parseIsoDate } from '@/utils/dates';

import { BookingConstraints } from './types';

export const CONSTRAINT_LABEL: Record<BookingConstraints['targetConstraint'], string> = {
  before: 'before',
  on: 'on',
  after: 'after',
  on_or_before: 'on or before',
  on_or_after: 'on or after'
};

const VALID_TARGET_DATE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_MODES = new Set<string>(['precise', 'approximate']);
const VALID_CONSTRAINTS = new Set(Object.keys(CONSTRAINT_LABEL));

export const buildConstraints = (params: URLSearchParams): BookingConstraints | null => {
  if (params.get('bookingConstraints') !== 'true') return null;
  const targetDate = params.get('targetDate');
  const targetMode = params.get('targetMode');
  const targetConstraint = params.get('targetConstraint');
  if (
    !targetDate ||
    !VALID_TARGET_DATE.test(targetDate) ||
    !VALID_MODES.has(targetMode ?? '') ||
    !VALID_CONSTRAINTS.has(targetConstraint ?? '')
  ) {
    return null;
  }
  return { targetDate, targetMode, targetConstraint } as BookingConstraints;
};

export const getConstraintBannerText = (c: BookingConstraints): string => {
  const prefix = c.targetMode === 'precise' ? 'Must book' : 'Prefer booking';
  const date = formatCalendarDate(c.targetDate);
  return `${prefix} ${CONSTRAINT_LABEL[c.targetConstraint]} ${date}`;
};

export const meetsConstraint = (date: string, constraints: BookingConstraints): boolean => {
  switch (constraints.targetConstraint) {
    case 'on':
      return date === constraints.targetDate;
    case 'before':
      return date < constraints.targetDate;
    case 'after':
      return date > constraints.targetDate;
    case 'on_or_before':
      return date <= constraints.targetDate;
    case 'on_or_after':
      return date >= constraints.targetDate;
  }
};

export const dayDiffFromTarget = (date: string, targetDate: string): number => {
  const a = parseIsoDate(date).getTime();
  const b = parseIsoDate(targetDate).getTime();
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
};

export const constraintPillLabel = (diff: number): string => {
  if (diff === 0) return 'Target date';
  const abs = Math.abs(diff);
  const unit = abs === 1 ? 'day' : 'days';
  return diff > 0 ? `+${abs} ${unit}` : `-${abs} ${unit}`;
};
